export const formatWithCommas = (value: string) => {
  const sanitizedValue = value.replace(/[^0-9]/g, '');
  return sanitizedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatToNaira = (amount?: number): string => {
  if (!amount) return '₦0.00';
  return `₦${amount?.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

export const formatNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(1) + 'm';
  } else if (value >= 1_000) {
    return (value / 1_000).toFixed(1) + 'k';
  } else {
    return value.toString();
  }
};
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.startsWith('234') && phoneNumber.length === 13) {
    return phoneNumber.replace(/^234/, '0');
  }
  return phoneNumber;
};
export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  let hours = date.getHours();
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert 0 → 12 and 13–23 → 1–11
  const formattedHours = `${hours}`.padStart(2, '0');

  return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
};
export const formatToISO8601 = (dateStr: string) => {
  try {
    if (!dateStr || dateStr === '') {
      const defaultDate = new Date();
      defaultDate.setMonth(defaultDate.getMonth() + 1);
      return defaultDate.toISOString();
    }

    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/').map(Number);

      // Use UTC methods to prevent timezone issues, set time to midnight UTC
      const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      return date.toISOString();
    } else if (dateStr.includes('T')) {
      // Already ISO, just ensure it has the correct format
      return new Date(dateStr).toISOString();
    } else if (dateStr.includes('-') && !dateStr.includes('T')) {
      // YYYY-MM-DD format, convert to full ISO with time set to midnight UTC
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      return date.toISOString();
    } else {
      const parsedDate = new Date(dateStr);
      if (isNaN(parsedDate.getTime())) {
        console.error('Invalid date format:', dateStr);
        const defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() + 1);
        return defaultDate.toISOString();
      }

      // Get the date components and create a UTC date with time set to midnight
      const year = parsedDate.getFullYear();
      const month = parsedDate.getMonth();
      const day = parsedDate.getDate();

      const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
      return date.toISOString();
    }
  } catch (error) {
    const defaultDate = new Date();
    defaultDate.setMonth(defaultDate.getMonth() + 1);
    return defaultDate.toISOString();
  }
};
export function toHHMMSS(timeInput: string | Date): string {
  let date: Date;

  // If it's already a Date, use it directly
  if (timeInput instanceof Date) {
    date = timeInput;
  } else {
    // If format is HH:MM (no seconds), append default seconds
    if (/^\d{2}:\d{2}$/.test(timeInput)) {
      timeInput = `${timeInput}:00`;
    }
    date = new Date(`1970-01-01T${timeInput}Z`);
  }

  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mm = String(date.getUTCMinutes()).padStart(2, '0');
  const ss = String(date.getUTCSeconds()).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
}
export const formatToDateString = (date: Date): string => {
  if (!date) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
export const standardizeDateFormat = (dateStr: string | undefined): string => {
  if (!dateStr) return '';

  try {
    // Helper function to format date to DD/MM/YYYY

    // Handle ISO format
    if (dateStr.includes('T')) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return formatToDateString(date);
      }
    }

    // Handle YYYY-MM-DD format
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return formatToDateString(date);
      }
    }

    // If already in DD/MM/YYYY format, validate it
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/').map(Number);
      const date = new Date(year, month - 1, day);

      // Check if it's a valid date
      if (
        !isNaN(date.getTime()) &&
        date.getDate() === day &&
        date.getMonth() === month - 1 &&
        date.getFullYear() === year
      ) {
        return dateStr; // Already in correct format
      }
    }

    // If we can't parse it in any standard way, try general parsing
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return formatToDateString(date);
    }

    return dateStr; // Return original if all parsing fails
  } catch (e) {
    return dateStr;
  }
};
export const parseAnyDateFormat = (
  dateString: string | undefined
): Date | null => {
  if (!dateString) return null;

  try {
    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/').map(Number);
      // Use UTC to avoid timezone issues
      const date = new Date(Date.UTC(year, month - 1, day));

      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Handle YYYY-MM-DD format
    if (dateString.includes('-')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Try generic Date parsing
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    console.error('Failed to parse date:', dateString);
    return null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};
export const formatDate = (dateString: string) => {
  const nf = new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return nf;
};
const calculateDurationInDays = (
  startDate: string,
  endDate: string
): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const formatDuration = (startDate: string, endDate: string): string => {
  const days = calculateDurationInDays(startDate, endDate);

  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else if (days < 30) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    if (remainingDays === 0) {
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    }
    return `${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
  } else if (days < 365) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }

    // If remaining days can form complete weeks
    if (remainingDays >= 7) {
      const weeks = Math.floor(remainingDays / 7);
      return `${months} month${months !== 1 ? 's' : ''} and ${weeks} week${weeks !== 1 ? 's' : ''}`;
    }

    return `${months} month${months !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;

    if (remainingDays === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }

    const months = Math.floor(remainingDays / 30);
    if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }

    return `${years} year${years !== 1 ? 's' : ''} and ${months} month${months !== 1 ? 's' : ''}`;
  }
};
export function formatDateToSubmit(dateStr: string) {
  const [day, month, year] = dateStr.split('/');
  const nd = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  return `${nd}T00:00:00`;
}

// Example
